import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../services/api";
import DashboardAdminBanner from "../../components/DashboardAdminBanner";

const C = {
  green:"#2D5A3D", greenMid:"#4A7C59", greenL:"#EAF4EE",
  gold:"#B86A10",  goldL:"#E8922A",    goldBg:"#FDF0D5",
  ink:"#2C1810",   inkMid:"#5C3D2E",   inkLight:"#9A7B60",
  cream:"#FDFAF3", creamD:"#F5EDD8",
};

function StatPill({ value, label, color }) {
  return (
    <div style={{
      background:"#fff", border:`1px solid ${C.creamD}`,
      borderLeft:`3px solid ${color}`,
      borderRadius:12, padding:"12px 16px",
      display:"flex", alignItems:"center", gap:12, flex:1, minWidth:120,
      boxShadow:"0 2px 10px rgba(0,0,0,0.05)",
    }}>
      <div>
        <div style={{fontSize:22,fontWeight:800,color:C.ink,fontFamily:"'Playfair Display',serif",lineHeight:1}}>
          {value ?? "—"}
        </div>
        <div style={{fontSize:11,fontWeight:600,color:C.inkMid,marginTop:2,fontFamily:"'Inter',sans-serif"}}>
          {label}
        </div>
      </div>
    </div>
  );
}

function PatientRow({ patient, index, consulted }) {
  return (
    <motion.div
      initial={{opacity:0,x:-8}} animate={{opacity:1,x:0}} transition={{delay:index*0.03}}
      style={{
        background:"#fff", borderRadius:12, padding:"12px 16px",
        border:`1px solid ${C.creamD}`,
        borderLeft:`4px solid ${consulted ? C.greenMid : "#3B82F6"}`,
        opacity: consulted ? 0.75 : 1,
        display:"flex", alignItems:"center", gap:14,
        boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
      }}
    >
      <div style={{
        width:32, height:32, borderRadius:8, flexShrink:0, fontWeight:700,
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:14, fontFamily:"'Inter',sans-serif",
        background: consulted ? C.greenL : "#EFF6FF",
        color: consulted ? C.greenMid : "#3B82F6",
        border:`1px solid ${consulted ? C.greenMid+"40" : "#BFDBFE"}`,
      }}>
        {consulted ? "✓" : index + 1}
      </div>

      <div style={{flex:1, minWidth:0}}>
        <div style={{fontWeight:700,color:C.ink,fontSize:14,fontFamily:"'Inter',sans-serif",
          whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {patient.name}
        </div>
        <div style={{fontSize:11,color:C.inkLight,marginTop:2,fontFamily:"'Inter',sans-serif"}}>
          {[patient.age ? `${patient.age} yrs` : null, patient.gender, patient.phone]
            .filter(Boolean).join(" · ")}
        </div>
      </div>

      <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        <span style={{
          fontSize:10,fontFamily:"monospace",
          background:C.creamD,color:C.inkMid,
          padding:"2px 8px",borderRadius:6,
        }}>
          {patient.patientId}
        </span>
        <span style={{
          fontSize:11,fontWeight:700,fontFamily:"'Inter',sans-serif",
          padding:"3px 10px",borderRadius:20,
          background: consulted ? "#D1FAE5" : "#EFF6FF",
          color: consulted ? "#065F46" : "#1D4ED8",
        }}>
          {consulted ? "Consulted" : "Pending"}
        </span>
      </div>
    </motion.div>
  );
}

function DoctorCard({ doc, selected, onClick }) {
  const pct = doc.todayTotal > 0
    ? Math.round((doc.todayConsulted / doc.todayTotal) * 100)
    : 0;

  return (
    <motion.div
      whileHover={{y:-1}}
      onClick={onClick}
      style={{
        background:"#fff",
        borderRadius:14,
        padding:"14px 16px",
        border:`2px solid ${selected ? "#3B82F6" : C.creamD}`,
        cursor:"pointer",
        boxShadow: selected
          ? "0 0 0 3px #BFDBFE"
          : "0 2px 10px rgba(0,0,0,0.05)",
        transition:"border-color 0.15s, box-shadow 0.15s",
      }}
    >
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10}}>
        <div>
          <div style={{fontWeight:700,color:C.ink,fontSize:14,fontFamily:"'Inter',sans-serif"}}>
            {doc.name}
          </div>
          <div style={{fontSize:11,color:C.inkLight,marginTop:2,fontFamily:"'Inter',sans-serif"}}>
            {doc.qualification} · {doc.clinicName}
          </div>
        </div>
        {selected && (
          <span style={{
            fontSize:10,fontWeight:700,background:"#EFF6FF",color:"#1D4ED8",
            border:"1px solid #BFDBFE",padding:"2px 8px",borderRadius:20,
            fontFamily:"'Inter',sans-serif",flexShrink:0,marginLeft:8,
          }}>Viewing</span>
        )}
      </div>

      <div style={{marginTop:6}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
          <span style={{fontSize:11,color:C.inkLight,fontFamily:"'Inter',sans-serif"}}>
            {doc.todayConsulted}/{doc.todayTotal} consulted
          </span>
          <span style={{fontSize:11,fontWeight:600,color:C.inkMid,fontFamily:"'Inter',sans-serif"}}>
            {pct}%
          </span>
        </div>
        <div style={{height:5,background:C.creamD,borderRadius:4,overflow:"hidden"}}>
          <div style={{
            height:"100%",
            width:`${pct}%`,
            background: pct === 100 ? C.greenMid : "#3B82F6",
            borderRadius:4,
            transition:"width 0.4s ease",
          }}/>
        </div>
        {doc.todayPending > 0 && (
          <div style={{fontSize:10,color:"#EF4444",marginTop:3,fontFamily:"'Inter',sans-serif"}}>
            {doc.todayPending} still pending
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function AdminOpdViewer() {
  const [doctors,        setDoctors]        = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [queue,          setQueue]          = useState([]);
  const [stats,          setStats]          = useState(null);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [loadingQueue,   setLoadingQueue]   = useState(false);
  const [lastRefresh,    setLastRefresh]    = useState(null);

  const loadDoctors = useCallback(async () => {
    try {
      const res = await api.get("/opd-admin/doctors");
      setDoctors(res.data || []);
    } catch (err) {
      console.error("AdminOpdViewer loadDoctors error:", err);
    } finally {
      setLoadingDoctors(false);
    }
  }, []);

  const loadQueue = useCallback(async (doctorId) => {
    if (!doctorId) return;
    setLoadingQueue(true);
    try {
      const [qRes, sRes] = await Promise.allSettled([
        api.get(`/opd-admin/queue/${doctorId}`),
        api.get(`/opd-admin/stats/${doctorId}`),
      ]);
      if (qRes.status === "fulfilled") setQueue(qRes.value.data.patients || []);
      if (sRes.status === "fulfilled") setStats(sRes.value.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("AdminOpdViewer loadQueue error:", err);
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  useEffect(() => { loadDoctors(); }, [loadDoctors]);

  useEffect(() => {
    if (!selectedDoctor) return;
    loadQueue(selectedDoctor._id);
    const t = setInterval(() => {
      loadQueue(selectedDoctor._id);
      loadDoctors();
    }, 90 * 1000);
    return () => clearInterval(t);
  }, [selectedDoctor, loadQueue, loadDoctors]);

  const pending   = queue.filter(p => !p.consulted);
  const consulted = queue.filter(p => p.consulted);

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday:"long", day:"numeric", month:"long",
  });

  const refreshTime = lastRefresh
    ? lastRefresh.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" })
    : null;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:24}}>

      <DashboardAdminBanner />

      <div>
        <h1 style={{fontSize:22,fontWeight:800,color:C.ink,fontFamily:"'Playfair Display',serif",margin:0}}>
          OPD Live Viewer
        </h1>
        <p style={{fontSize:13,color:C.inkLight,fontFamily:"'Inter',sans-serif",margin:"4px 0 0"}}>
          {todayLabel} · Read-only view of any doctor's active queue
          {refreshTime && (
            <span style={{marginLeft:8,color:"#9CA3AF"}}>· Last updated {refreshTime}</span>
          )}
        </p>
      </div>

      <div style={{
        background:"#FFF7ED",border:"1px solid #FED7AA",
        borderRadius:12,padding:"10px 16px",
        display:"flex",alignItems:"center",gap:10,
      }}>
        <span style={{fontSize:16}}>🔒</span>
        <p style={{margin:0,fontSize:12,color:"#92400E",fontFamily:"'Inter',sans-serif"}}>
          Patient data is scoped per doctor. Each doctor can only see their own patients.
          This view is restricted to Master Admin and Dashboard Admin roles.
        </p>
      </div>

      <div>
        <div style={{fontSize:11,fontWeight:700,color:C.inkLight,letterSpacing:"0.1em",
          textTransform:"uppercase",marginBottom:12,fontFamily:"'Inter',sans-serif",
          display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span>Select a doctor to monitor</span>
          <button
            onClick={() => { loadDoctors(); if (selectedDoctor) loadQueue(selectedDoctor._id); }}
            style={{
              fontSize:11,padding:"4px 10px",borderRadius:8,border:`1px solid ${C.creamD}`,
              background:"#fff",color:C.inkMid,cursor:"pointer",fontFamily:"'Inter',sans-serif",
            }}
          >
            ↻ Refresh
          </button>
        </div>

        {loadingDoctors ? (
          <div style={{textAlign:"center",padding:32,color:C.inkLight,fontFamily:"'Inter',sans-serif"}}>
            Loading doctors…
          </div>
        ) : doctors.length === 0 ? (
          <div style={{textAlign:"center",padding:32,color:C.inkLight,fontFamily:"'Inter',sans-serif"}}>
            No active doctors found.
          </div>
        ) : (
          <div style={{display:"grid",gap:12,gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))"}}>
            {doctors.map(doc => (
              <DoctorCard
                key={doc._id}
                doc={doc}
                selected={selectedDoctor?._id === doc._id}
                onClick={() => setSelectedDoctor(doc)}
              />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedDoctor && (
          <motion.div
            key={selectedDoctor._id}
            initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:8}}
            style={{display:"flex",flexDirection:"column",gap:20}}
          >
            {stats && (
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <StatPill value={stats.todayTotal}     label="Today's Queue" color="#3B82F6"/>
                <StatPill value={stats.todayConsulted} label="Consulted"     color={C.greenMid}/>
                <StatPill value={stats.todayPending}   label="Pending"       color="#EF4444"/>
                <StatPill value={stats.rxToday}        label="Prescriptions" color={C.gold}/>
                <StatPill value={stats.weekPatients}   label="This Week"     color="#8B5CF6"/>
              </div>
            )}

            <div style={{
              background:"#fff",borderRadius:18,
              border:`1px solid ${C.creamD}`,overflow:"hidden",
              boxShadow:"0 4px 20px rgba(0,0,0,0.06)",
            }}>
              <div style={{
                padding:"14px 20px",
                background:`linear-gradient(135deg,#EFF6FF,${C.cream})`,
                borderBottom:`1px solid #BFDBFE40`,
                display:"flex",alignItems:"center",justifyContent:"space-between",
              }}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:18}}>🩺</span>
                  <div>
                    <div style={{fontWeight:700,fontSize:15,color:C.ink,fontFamily:"'Inter',sans-serif"}}>
                      {selectedDoctor.name}'s Queue
                    </div>
                    <div style={{fontSize:11,color:C.inkLight,fontFamily:"'Inter',sans-serif"}}>
                      {selectedDoctor.qualification} · {selectedDoctor.clinicName}
                    </div>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {loadingQueue && (
                    <span style={{fontSize:11,color:C.inkLight,fontFamily:"'Inter',sans-serif"}}>Refreshing…</span>
                  )}
                  <span style={{
                    fontSize:11,fontWeight:600,fontFamily:"'Inter',sans-serif",
                    background:"#FEF3C7",color:"#92400E",
                    padding:"3px 10px",borderRadius:20,
                    border:"1px solid #FDE68A",
                  }}>
                    Read-only
                  </span>
                </div>
              </div>

              <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:20}}>

                {pending.length > 0 && (
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:C.inkLight,letterSpacing:"0.1em",
                      textTransform:"uppercase",marginBottom:10,fontFamily:"'Inter',sans-serif"}}>
                      Pending ({pending.length})
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {pending.map((p, i) => (
                        <PatientRow key={p._id} patient={p} index={i} consulted={false}/>
                      ))}
                    </div>
                  </div>
                )}

                {consulted.length > 0 && (
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:C.inkLight,letterSpacing:"0.1em",
                      textTransform:"uppercase",marginBottom:10,fontFamily:"'Inter',sans-serif"}}>
                      Consulted ({consulted.length})
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {consulted.map((p, i) => (
                        <PatientRow key={p._id} patient={p} index={i} consulted={true}/>
                      ))}
                    </div>
                  </div>
                )}

                {!loadingQueue && queue.length === 0 && (
                  <div style={{textAlign:"center",padding:"32px 0",color:C.inkLight,fontFamily:"'Inter',sans-serif"}}>
                    <div style={{fontSize:32,marginBottom:8}}>🏥</div>
                    No patients in queue for {selectedDoctor.name} today.
                  </div>
                )}

              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!selectedDoctor && !loadingDoctors && (
        <div style={{
          background:"#fff",borderRadius:18,padding:"48px 24px",textAlign:"center",
          border:`1px dashed ${C.creamD}`,color:C.inkLight,
        }}>
          <div style={{fontSize:40,marginBottom:12}}>👆</div>
          <div style={{fontWeight:600,color:C.inkMid,fontFamily:"'Inter',sans-serif"}}>
            Select a doctor above to view their live queue
          </div>
          <div style={{fontSize:13,color:C.inkLight,marginTop:6,fontFamily:"'Inter',sans-serif"}}>
            The queue refreshes automatically every 90 seconds
          </div>
        </div>
      )}

    </div>
  );
}